import { useEffect, useMemo, useRef, useState } from "react";
import {
  getAccessScopeService,
  searchGlobalService,
} from "api/services/globalSearchServices";
import { getFilterConfig } from "api/services/usersServices";
import { getObligationGroupsStatusCounts } from "api/services/obligationsServices";
import useDebouncedValue from "hooks/useDebouncedValue";
import {
  buildGlobalSearchPayload,
  getGlobalSearchPageState,
  GLOBAL_SEARCH_TYPES,
  isFilesSearchType,
  mergeGlobalSearchResults,
  normalizeGlobalSearchResponse,
  normalizeRecentlyViewedItem,
} from "./globalSearchUtils";

const MIN_CHARS = 3;
const MAX_CHARS = 100;
const SEARCH_PAGE_SIZE = 50;
export const SEARCH_DEBOUNCE_MS = 450;
export const FILTER_DEBOUNCE_MS = 350;

const RECENTS_KEY = "global_search_recently_viewed_v1";
const MAX_RECENT_PER_TYPE = 10;

let ACCESS_SCOPE_CACHE = null;
let ACCESS_SCOPE_PROMISE = null;
let ENTITIES_CACHE = null;
let ENTITIES_PROMISE = null;

const obligationGroupId = (item) => {
  if (item?.type !== GLOBAL_SEARCH_TYPES.OBLIGATIONS) return null;
  return item.group_id || item.id || null;
};

const recentItemKey = (item) => {
  const normalized = normalizeRecentlyViewedItem(item);
  if (!normalized || typeof normalized !== "object") return "";
  if (isFilesSearchType(normalized.type)) {
    const id = normalized.evidence_id || normalized.id;
    return id ? `${GLOBAL_SEARCH_TYPES.FILES}:${id}` : "";
  }
  if (normalized.type === GLOBAL_SEARCH_TYPES.OBLIGATIONS) {
    const id = normalized.group_id || normalized.id;
    return id ? `${GLOBAL_SEARCH_TYPES.OBLIGATIONS}:${id}` : "";
  }
  return "";
};

/** Dedupe by stable identity, then keep up to 10 obligations and 10 files. */
const enforceRecentCaps = (list) => {
  const seen = new Set();
  let obligationCount = 0;
  let fileCount = 0;
  return (Array.isArray(list) ? list : [])
    .map(normalizeRecentlyViewedItem)
    .filter((item) => {
      const key = recentItemKey(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      if (item?.type === GLOBAL_SEARCH_TYPES.OBLIGATIONS) {
        if (obligationCount >= MAX_RECENT_PER_TYPE) return false;
        obligationCount += 1;
        return true;
      }
      if (isFilesSearchType(item?.type)) {
        if (fileCount >= MAX_RECENT_PER_TYPE) return false;
        fileCount += 1;
        return true;
      }
      return false;
    });
};

const loadRecentlyViewedFromStorage = () => {
  try {
    if (typeof window === "undefined") return [];
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return enforceRecentCaps(parsed);
  } catch {
    return [];
  }
};

export default function useGlobalSearchBar() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const debouncedQuery = useDebouncedValue(value, SEARCH_DEBOUNCE_MS);
  const [helpText, setHelpText] = useState("");
  const [emptySubmitHint, setEmptySubmitHint] = useState(false);
  const [selectedType, setSelectedType] = useState(GLOBAL_SEARCH_TYPES.ALL);
  const [loading, setLoading] = useState(false);
  const [debouncing, setDebouncing] = useState(false);
  const [groupStatusMap, setGroupStatusMap] = useState({});
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [searchHasMoreServer, setSearchHasMoreServer] = useState(false);
  const [loadingMoreResults, setLoadingMoreResults] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [recentlyViewed, setRecentlyViewed] = useState(loadRecentlyViewedFromStorage);

  const [accessScopeUsers, setAccessScopeUsers] = useState([]);
  const [accessScopeFrameworks, setAccessScopeFrameworks] = useState([]);
  const [accessScopeEntities, setAccessScopeEntities] = useState([]);

  const [selectedFrameworkKeys, setSelectedFrameworkKeys] = useState([]);
  const [selectedResponsibleKeys, setSelectedResponsibleKeys] = useState([]);
  const [selectedEntityKeys, setSelectedEntityKeys] = useState([]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  const [serverResults, setServerResults] = useState([]);

  const wrapperRef = useRef(null);
  const panelRef = useRef(null);
  const suppressOpenFromClearRef = useRef(false);
  const activeSearchControllerRef = useRef(null);
  const searchSequenceRef = useRef(0);
  const lastRequestedSearchRef = useRef(null);
  const statusCountsControllerRef = useRef(null);
  const loadedStatusIdsRef = useRef(new Set());
  const selectedFrameworkKeysRef = useRef(selectedFrameworkKeys);
  const selectedResponsibleKeysRef = useRef(selectedResponsibleKeys);
  const selectedEntityKeysRef = useRef(selectedEntityKeys);
  const accessScopeFrameworksRef = useRef(accessScopeFrameworks);
  const accessScopeUsersRef = useRef(accessScopeUsers);
  const accessScopeEntitiesRef = useRef(accessScopeEntities);
  const nextSearchCursorRef = useRef(null);
  const activeSearchPayloadRef = useRef(null);
  const loadingMoreResultsRef = useRef(false);
  const filtersHydratedRef = useRef(false);
  const frameworksSelectionInitializedRef = useRef(false);
  const responsiblesSelectionInitializedRef = useRef(false);
  const entitiesSelectionInitializedRef = useRef(false);

  selectedFrameworkKeysRef.current = selectedFrameworkKeys;
  selectedResponsibleKeysRef.current = selectedResponsibleKeys;
  selectedEntityKeysRef.current = selectedEntityKeys;
  accessScopeFrameworksRef.current = accessScopeFrameworks;
  accessScopeUsersRef.current = accessScopeUsers;
  accessScopeEntitiesRef.current = accessScopeEntities;

  const activeFiltersKey = useMemo(
    () =>
      JSON.stringify([
        [...selectedFrameworkKeys].sort(),
        [...selectedResponsibleKeys].sort(),
        [...selectedEntityKeys].sort(),
      ]),
    [selectedEntityKeys, selectedFrameworkKeys, selectedResponsibleKeys]
  );
  const debouncedFiltersKey = useDebouncedValue(activeFiltersKey, FILTER_DEBOUNCE_MS);

  const invalidateActiveSearch = () => {
    searchSequenceRef.current += 1;
    activeSearchControllerRef.current?.abort();
    activeSearchControllerRef.current = null;
    statusCountsControllerRef.current?.abort();
    statusCountsControllerRef.current = null;
    loadingMoreResultsRef.current = false;
  };

  const isMac = useMemo(() => {
    const platform =
      navigator.userAgentData?.platform || navigator.platform || navigator.userAgent;
    return /mac/i.test(platform);
  }, []);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(RECENTS_KEY, JSON.stringify(recentlyViewed || []));
    } catch {
    }
  }, [recentlyViewed]);

  const loadAccessScope = async () => {
    try {
      if (ACCESS_SCOPE_CACHE) {
        setAccessScopeUsers(ACCESS_SCOPE_CACHE.users || []);
        setAccessScopeFrameworks(ACCESS_SCOPE_CACHE.frameworks || []);
        return;
      }

      if (!ACCESS_SCOPE_PROMISE) {
        ACCESS_SCOPE_PROMISE = getAccessScopeService();
      }

      const data = await ACCESS_SCOPE_PROMISE;
      ACCESS_SCOPE_CACHE = data;

      setAccessScopeUsers((data && data.users) || []);
      setAccessScopeFrameworks((data && data.frameworks) || []);
    } catch (err) {
      console.error(err);
      ACCESS_SCOPE_PROMISE = null;
    }
  };

  const loadEntities = async () => {
    try {
      if (ENTITIES_CACHE) {
        setAccessScopeEntities(ENTITIES_CACHE);
        return;
      }

      if (!ENTITIES_PROMISE) {
        ENTITIES_PROMISE = getFilterConfig();
      }

      const data = await ENTITIES_PROMISE;
      const sections = Array.isArray(data?.sections)
        ? data.sections.map((section) => ({
            id: section.id || section._id,
            name: section.name || section.label || "Entity",
          }))
        : [];

      ENTITIES_CACHE = sections;
      setAccessScopeEntities(sections);
    } catch (err) {
      console.error(err);
      ENTITIES_PROMISE = null;
    }
  };

  useEffect(() => {
    if (!accessScopeFrameworks.length) return;
    if (selectedFrameworkKeys.length > 0) {
      frameworksSelectionInitializedRef.current = true;
      return;
    }
    // Default-select only on first scope load — do not undo an explicit Clear.
    if (frameworksSelectionInitializedRef.current) return;
    frameworksSelectionInitializedRef.current = true;
    setSelectedFrameworkKeys(
      accessScopeFrameworks.map((fw) => fw.id || fw.name)
    );
  }, [accessScopeFrameworks, selectedFrameworkKeys.length]);

  useEffect(() => {
    if (!accessScopeUsers.length) return;
    if (selectedResponsibleKeys.length > 0) {
      responsiblesSelectionInitializedRef.current = true;
      return;
    }
    if (responsiblesSelectionInitializedRef.current) return;
    responsiblesSelectionInitializedRef.current = true;
    setSelectedResponsibleKeys(accessScopeUsers.map((u) => u.id || u.name));
  }, [accessScopeUsers, selectedResponsibleKeys.length]);

  useEffect(() => {
    if (!accessScopeEntities.length) return;
    if (selectedEntityKeys.length > 0) {
      entitiesSelectionInitializedRef.current = true;
      return;
    }
    if (entitiesSelectionInitializedRef.current) return;
    entitiesSelectionInitializedRef.current = true;
    setSelectedEntityKeys(
      accessScopeEntities.map((entity) => entity.id || entity.name)
    );
  }, [accessScopeEntities, selectedEntityKeys.length]);

  // Mark filters hydrated once both scope lists have been applied (or are empty).
  useEffect(() => {
    if (filtersHydratedRef.current) return;
    const frameworksReady =
      accessScopeFrameworks.length === 0 || selectedFrameworkKeys.length > 0;
    const usersReady =
      accessScopeUsers.length === 0 || selectedResponsibleKeys.length > 0;
    if (!frameworksReady || !usersReady) return;
    // Empty scopes hydrate immediately so debounce search works before access-scope loads.
    // When select-all later fills keys, requestKey dedupe skips a no-op re-search
    // (resolveSearchFilterIds omits filters when all are selected).
    filtersHydratedRef.current = true;
    setFiltersHydrated(true);
  }, [
    accessScopeFrameworks.length,
    accessScopeUsers.length,
    selectedFrameworkKeys.length,
    selectedResponsibleKeys.length,
  ]);

  const markSuppressOpenFromClear = () => {
    suppressOpenFromClearRef.current = true;
  };

  const handleOpen = () => {
    if (suppressOpenFromClearRef.current) {
      suppressOpenFromClearRef.current = false;
      return;
    }
    setOpen(true);
    loadAccessScope();
    loadEntities();
  };

  const handleClose = () => {
    setOpen(false);
    setFiltersOpen(false);
  };

  const getFrameworkCode = (name) =>
    (String(name || "").match(/[A-Za-z0-9]+/g) || [])
      .slice(0, 3)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "FW";

  const addToRecentlyViewed = (item) => {
    const normalized = normalizeRecentlyViewedItem(item);
    const key = recentItemKey(normalized);
    if (!key) return;

    setRecentlyViewed((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const existingIndex = list.findIndex((x) => recentItemKey(x) === key);

      // Already stored: only move to front — never drop siblings.
      if (existingIndex >= 0) {
        const existing = list[existingIndex];
        const rest = list.filter((_, i) => i !== existingIndex);
        return [{ ...existing, ...normalized }, ...rest];
      }

      // New item: prepend and apply per-type caps.
      return enforceRecentCaps([normalized, ...list]);
    });
  };

  const clearRecentlyViewed = () => setRecentlyViewed([]);

  const toggleFrameworkKey = (key) => {
    setSelectedFrameworkKeys((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== key);
      }
      return [...prev, key];
    });
  };

  const toggleResponsibleKey = (key) => {
    setSelectedResponsibleKeys((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== key);
      }
      return [...prev, key];
    });
  };

  const toggleEntityKey = (key) => {
    setSelectedEntityKeys((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== key);
      }
      return [...prev, key];
    });
  };

  const selectAllFrameworkKeys = (keys) => {
    const add = (keys || []).filter(Boolean);
    if (!add.length) return;
    setSelectedFrameworkKeys((prev) => {
      const next = new Set(prev);
      add.forEach((k) => next.add(k));
      return Array.from(next);
    });
  };

  const selectAllEntityKeys = (keys) => {
    const add = (keys || []).filter(Boolean);
    if (!add.length) return;
    setSelectedEntityKeys((prev) => {
      const next = new Set(prev);
      add.forEach((k) => next.add(k));
      return Array.from(next);
    });
  };

  const selectAllResponsibleKeys = (keys) => {
    const add = (keys || []).filter(Boolean);
    if (!add.length) return;
    setSelectedResponsibleKeys((prev) => {
      const next = new Set(prev);
      add.forEach((k) => next.add(k));
      return Array.from(next);
    });
  };

  const deselectFrameworkKeys = (keys) => {
    const remove = new Set((keys || []).filter(Boolean));
    if (!remove.size) return;
    setSelectedFrameworkKeys((prev) => prev.filter((k) => !remove.has(k)));
  };

  const deselectEntityKeys = (keys) => {
    const remove = new Set((keys || []).filter(Boolean));
    if (!remove.size) return;
    setSelectedEntityKeys((prev) => prev.filter((k) => !remove.has(k)));
  };

  const deselectResponsibleKeys = (keys) => {
    const remove = new Set((keys || []).filter(Boolean));
    if (!remove.size) return;
    setSelectedResponsibleKeys((prev) => prev.filter((k) => !remove.has(k)));
  };

  const clearFrameworkKeys = (keepKey) => {
    setSelectedFrameworkKeys(keepKey != null ? [keepKey] : []);
  };

  const clearEntityKeys = (keepKey) => {
    setSelectedEntityKeys(keepKey != null ? [keepKey] : []);
  };

  const clearResponsibleKeys = (keepKey) => {
    setSelectedResponsibleKeys(keepKey != null ? [keepKey] : []);
  };

  const hasSubmittedSearch = submittedQuery.trim().length >= MIN_CHARS;
  const valueMatchesSubmitted =
    value.trim().length >= MIN_CHARS && value.trim() === submittedQuery.trim();
  // Keep results mode while debouncing so rows are not unmounted every keystroke.
  // While a fetch is in flight, swap to skeleton so stale rows never look current.
  const showResults =
    hasSubmittedSearch &&
    (valueMatchesSubmitted ||
      ((debouncing || loading) && serverResults.length > 0));
  const showResultsSkeleton = loading && hasSubmittedSearch;

  const buildSearchPayload = (
    term,
    selectedTypeOverride,
    { cursor = null, limit = SEARCH_PAGE_SIZE } = {}
  ) => {
    return buildGlobalSearchPayload({
      q: term,
      selectedType: selectedTypeOverride,
      selectedFrameworkKeys: selectedFrameworkKeysRef.current,
      accessScopeFrameworks: accessScopeFrameworksRef.current,
      selectedResponsibleKeys: selectedResponsibleKeysRef.current,
      accessScopeUsers: accessScopeUsersRef.current,
      selectedEntityKeys: selectedEntityKeysRef.current,
      accessScopeEntities: accessScopeEntitiesRef.current,
      cursor,
      limit,
    });
  };

  const runSearch = async (
    q,
    {
      showMinCharsHelp = true,
      selectedTypeOverride = selectedType,
    } = {}
  ) => {
    const term = (q || "").trim();

    setOpen(true);

    if (term.length < MIN_CHARS) {
      invalidateActiveSearch();
      setLoading(false);
      setDebouncing(false);
      setServerResults([]);
      setGroupStatusMap({});
      setSubmittedQuery("");
      setSearchHasMoreServer(false);
      setLoadingMoreResults(false);
      setLoadMoreError(false);
      nextSearchCursorRef.current = null;
      activeSearchPayloadRef.current = null;
      setHelpText("");

      // Empty Enter/Search: nudge once. 1–2 chars use derived showMinCharsHint.
      if (showMinCharsHelp && term.length === 0) {
        setEmptySubmitHint(true);
      }
      return;
    }

    setDebouncing(false);
    setHelpText("");
    setEmptySubmitHint(false);
    setSubmittedQuery(term);
    setSearchHasMoreServer(false);
    setLoadingMoreResults(false);
    loadingMoreResultsRef.current = false;
    setLoadMoreError(false);
    nextSearchCursorRef.current = null;

    const requestSequence = searchSequenceRef.current + 1;
    searchSequenceRef.current = requestSequence;
    activeSearchControllerRef.current?.abort();
    statusCountsControllerRef.current?.abort();
    statusCountsControllerRef.current = null;
    const controller = new AbortController();
    activeSearchControllerRef.current = controller;

    try {
      setLoading(true);
      setGroupStatusMap({});
      loadedStatusIdsRef.current = new Set();
      const payload = buildSearchPayload(term, selectedTypeOverride);
      lastRequestedSearchRef.current = JSON.stringify(payload);
      activeSearchPayloadRef.current = payload;

      const data = await searchGlobalService(payload, { signal: controller.signal });
      if (requestSequence !== searchSequenceRef.current) return;

      const normalized = normalizeGlobalSearchResponse(data);
      const pageState = getGlobalSearchPageState(data);
      setServerResults(normalized);
      nextSearchCursorRef.current = pageState.nextCursor;
      setSearchHasMoreServer(pageState.hasMore);
    } catch (err) {
      if (err?.name === "AbortError") return;
      if (requestSequence !== searchSequenceRef.current) return;
      console.error(err);
      setServerResults([]);
      setGroupStatusMap({});
      setSearchHasMoreServer(false);
      nextSearchCursorRef.current = null;
      activeSearchPayloadRef.current = null;
      setHelpText("global_search.messages.search_failed");
    } finally {
      if (requestSequence === searchSequenceRef.current) {
        setLoading(false);
        if (activeSearchControllerRef.current === controller) {
          activeSearchControllerRef.current = null;
        }
      }
    }
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);

    const term = next.trim();
    setEmptySubmitHint(false);
    setHelpText("");

    if (term.length < MIN_CHARS) {
      invalidateActiveSearch();
      lastRequestedSearchRef.current = null;
      setLoading(false);
      setDebouncing(false);
      setServerResults([]);
      setGroupStatusMap({});
      loadedStatusIdsRef.current = new Set();
      setSubmittedQuery("");
      setSearchHasMoreServer(false);
      setLoadingMoreResults(false);
      setLoadMoreError(false);
      nextSearchCursorRef.current = null;
      activeSearchPayloadRef.current = null;
      return;
    }

    // Typing toward a new query: mark debounce pending, keep prior results until runSearch.
    invalidateActiveSearch();
    lastRequestedSearchRef.current = null;
    setLoading(false);
    setLoadingMoreResults(false);
    setLoadMoreError(false);
    setSearchHasMoreServer(false);
    nextSearchCursorRef.current = null;
    activeSearchPayloadRef.current = null;
    setDebouncing(true);
  };

  const showMinCharsHint =
    open &&
    ((value.trim().length > 0 && value.trim().length < MIN_CHARS) ||
      emptySubmitHint);

  const showMaxCharsHint = open && value.length >= MAX_CHARS;

  const handleBlur = () => {
    if (!open) return;
    if (value.trim().length < MIN_CHARS) {
      setEmptySubmitHint(true);
    }
  };

  const handleSearchClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    return runSearch(value, { showMinCharsHelp: true });
  };

  const handlePressEnter = (e) => {
    e.preventDefault();
    return runSearch(value, { showMinCharsHelp: true });
  };

  const handleTypeSelect = (nextType) => {
    if (nextType === selectedType) return;

    setSelectedType(nextType);

    const term = value.trim();
    const hasActiveSearch =
      term.length >= MIN_CHARS && term === submittedQuery.trim();
    if (hasActiveSearch) {
      return runSearch(term, {
        showMinCharsHelp: false,
        selectedTypeOverride: nextType,
      });
    }

    return undefined;
  };

  const loadMoreSearchResults = async () => {
    const term = submittedQuery.trim();
    if (
      term.length < MIN_CHARS ||
      term !== value.trim() ||
      loading ||
      loadingMoreResultsRef.current ||
      !searchHasMoreServer
    ) {
      return;
    }

    const activePayload = activeSearchPayloadRef.current;
    if (!activePayload || activePayload.q !== term) {
      setSearchHasMoreServer(false);
      return;
    }
    const cursor = nextSearchCursorRef.current;
    if (!cursor) {
      setSearchHasMoreServer(false);
      return;
    }
    const requestSequence = searchSequenceRef.current + 1;
    searchSequenceRef.current = requestSequence;
    activeSearchControllerRef.current?.abort();
    const controller = new AbortController();
    activeSearchControllerRef.current = controller;
    loadingMoreResultsRef.current = true;

    try {
      setLoadingMoreResults(true);
      setLoadMoreError(false);
      const payload = {
        ...activePayload,
        cursor,
        limit: SEARCH_PAGE_SIZE,
      };
      const data = await searchGlobalService(payload, {
        signal: controller.signal,
      });
      if (requestSequence !== searchSequenceRef.current) return;

      const normalized = normalizeGlobalSearchResponse(data);
      const pageState = getGlobalSearchPageState(data);
      const nextResults = mergeGlobalSearchResults(serverResults, normalized);

      nextSearchCursorRef.current = pageState.nextCursor;
      setServerResults(nextResults);
      setSearchHasMoreServer(pageState.hasMore);
    } catch (err) {
      if (err?.name === "AbortError") return;
      if (requestSequence !== searchSequenceRef.current) return;
      console.error("Failed to load more search results", err);
      setLoadMoreError(true);
    } finally {
      if (requestSequence === searchSequenceRef.current) {
        loadingMoreResultsRef.current = false;
        setLoadingMoreResults(false);
        if (activeSearchControllerRef.current === controller) {
          activeSearchControllerRef.current = null;
        }
      }
    }
  };

  const handleViewMore = () => {
    if (loadingMoreResultsRef.current) return undefined;
    return loadMoreSearchResults();
  };

  useEffect(() => {
    const term = debouncedQuery.trim();
    if (term.length < MIN_CHARS || term !== value.trim()) return;
    if (!filtersHydrated) return;

    const requestKey = JSON.stringify(buildSearchPayload(term, selectedType));
    if (lastRequestedSearchRef.current === requestKey) return;

    setDebouncing(false);
    runSearch(term, {
      showMinCharsHelp: false,
      selectedTypeOverride: selectedType,
    });
  }, [debouncedQuery, selectedType, debouncedFiltersKey, filtersHydrated]);

  const searchResults = useMemo(() => {
    if (!showResults && !showResultsSkeleton) return [];
    return serverResults;
  }, [showResults, showResultsSkeleton, serverResults]);

  const visibleResults = searchResults;

  const hasMoreResults = showResults && searchHasMoreServer;
  const searchPending = loading || debouncing;
  const showEndOfResultsHint =
    showResults &&
    !searchPending &&
    !loadingMoreResults &&
    searchResults.length > 0 &&
    !searchHasMoreServer;

  const filteredRecents = useMemo(() => {
    let list = enforceRecentCaps(recentlyViewed);
    if (selectedType === GLOBAL_SEARCH_TYPES.OBLIGATIONS) {
      list = list.filter((x) => x.type === GLOBAL_SEARCH_TYPES.OBLIGATIONS);
    } else if (selectedType === GLOBAL_SEARCH_TYPES.FILES) {
      list = list.filter((x) => isFilesSearchType(x.type));
    }
    return list;
  }, [recentlyViewed, selectedType]);

  // Rows currently painted in the left list (search hits or recently viewed).
  const statusCountSourceItems = showResults ? visibleResults : filteredRecents;

  // Idle for search hits, or idle recents panel (no in-flight/debounced search).
  const statusCountsIdle =
    open &&
    !loading &&
    !loadingMoreResults &&
    !debouncing &&
    (showResults ? valueMatchesSubmitted : true);

  const statusCountsPending = useMemo(() => {
    if (!open) return false;
    return statusCountSourceItems.some((item) => {
      const groupId = obligationGroupId(item);
      return groupId && !groupStatusMap[groupId];
    });
  }, [open, statusCountSourceItems, groupStatusMap]);

  // Status counts when UI is idle; visible obligation groups only (hits or recents).
  useEffect(() => {
    if (!statusCountsIdle) return;

    const missingGroupIds = Array.from(
      new Set(
        statusCountSourceItems
          .map((item) => obligationGroupId(item))
          .filter((id) => id && !loadedStatusIdsRef.current.has(id))
      )
    );

    if (missingGroupIds.length === 0) return;

    missingGroupIds.forEach((id) => loadedStatusIdsRef.current.add(id));

    let cancelled = false;
    let completed = false;
    let idleId = 0;
    let raf1 = 0;
    let raf2 = 0;
    const controller = new AbortController();
    statusCountsControllerRef.current?.abort();
    statusCountsControllerRef.current = controller;

    const fetchCounts = async () => {
      try {
        const statusRes = await getObligationGroupsStatusCounts({
          group_ids: missingGroupIds,
        });
        if (cancelled || controller.signal.aborted) return;

        const map = {};
        (statusRes?.groups || []).forEach((g) => {
          if (g?.group_id) map[g.group_id] = g;
        });
        // Ensure every requested id leaves the skeleton state, even if API omitted it.
        missingGroupIds.forEach((id) => {
          if (!map[id]) map[id] = {};
        });

        setGroupStatusMap((prev) => ({ ...prev, ...map }));
        completed = true;
      } catch (err) {
        if (!cancelled && err?.name !== "AbortError") {
          missingGroupIds.forEach((id) => loadedStatusIdsRef.current.delete(id));
          console.error("Failed to fetch group status counts", err);
        }
      } finally {
        if (statusCountsControllerRef.current === controller) {
          statusCountsControllerRef.current = null;
        }
      }
    };

    const scheduleFetch = () => {
      if (cancelled) return;
      void fetchCounts();
    };

    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(scheduleFetch, { timeout: 500 });
    } else {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(scheduleFetch);
      });
    }

    return () => {
      cancelled = true;
      if (idleId && typeof window !== "undefined" && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      controller.abort();
      // Keep IDs in loadedStatusIdsRef on abort so typing does not re-fetch the same groups.
      // Only failed (non-abort) requests remove IDs above.
      if (!completed && controller.signal.aborted) {
        // no-op: leave IDs marked as in-flight/loaded to prevent storm
      }
    };
  }, [statusCountsIdle, statusCountSourceItems]);

  useEffect(() => {
    if (open) {
      loadAccessScope();
      loadEntities();
    }
  }, [open]);

  useEffect(
    () => () => {
      invalidateActiveSearch();
    },
    []
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      const rawKey = e?.key;
      const key = typeof rawKey === "string" ? rawKey.toLowerCase() : "";
      const shift = !!e?.shiftKey;

      const macCombo = isMac && !!e?.metaKey && shift && key === "k";
      const winCombo = !isMac && !!e?.ctrlKey && shift && key === "k";

      if (macCombo || winCombo) {
        e.preventDefault();
        handleOpen();
        setTimeout(() => {
          document.getElementById("global-search-input")?.focus();
        }, 0);
        return;
      }

      if (key === "escape") {
        if (document.querySelector(".ant-drawer-open")) return;
        if (filtersOpen) {
          setFiltersOpen(false);
          return;
        }
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMac, filtersOpen]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e) => {
      const wrapper = wrapperRef.current;
      const target = e.target;
      const inAntDrawer =
        target?.closest?.(".ant-drawer") ||
        target?.closest?.(".ant-drawer-mask") ||
        target?.closest?.(".ant-drawer-wrap") ||
        target?.closest?.(".ant-drawer-content-wrapper");

      if (inAntDrawer) return;
      if (wrapper && wrapper.contains(target)) return;

      setOpen(false);
      setFiltersOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
    };
  }, [open]);

  return {
    MIN_CHARS,
    MAX_CHARS,
    loading,
    searchPending,

    open,
    value,
    helpText,
    showMinCharsHint,
    showMaxCharsHint,
    selectedType,
    filtersOpen,
    setFiltersOpen,

    wrapperRef,
    panelRef,

    isMac,
    showResults,
    showResultsSkeleton,
    searchResults,
    visibleResults,
    hasMoreResults,
    showEndOfResultsHint,
    loadingMoreResults,
    loadMoreError,

    filteredRecents,

    handleOpen,
    handleClose,
    handleChange,
    handleBlur,
    handlePressEnter,
    handleSearchClick,
    handleTypeSelect,
    handleViewMore,
    markSuppressOpenFromClear,

    addToRecentlyViewed,
    clearRecentlyViewed,
    getFrameworkCode,

    accessScopeUsers,
    accessScopeFrameworks,
    accessScopeEntities,

    selectedFrameworkKeys,
    selectedResponsibleKeys,
    selectedEntityKeys,
    groupStatusMap,
    statusCountsPending,
    toggleFrameworkKey,
    toggleResponsibleKey,
    toggleEntityKey,
    selectAllFrameworkKeys,
    selectAllEntityKeys,
    selectAllResponsibleKeys,
    deselectFrameworkKeys,
    deselectEntityKeys,
    deselectResponsibleKeys,
    clearFrameworkKeys,
    clearEntityKeys,
    clearResponsibleKeys,
  };
}
